import moment from 'moment';
import { useMemo, useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';
import { TaskProposal, TaskProposalEdits, TaskProposalType } from 'src/services/ai/ai-enrichment';

import { TASK_TYPE_LABELS } from './format-finding';

type Option = { id: string; name: string };

type Props = {
  proposal: TaskProposal | null;
  onClose: VoidFunction;
  onAccept: (edits: TaskProposalEdits) => Promise<void> | void;
  saving?: boolean;
};

/**
 * Change a proposed task before accepting it (JT1-1766): title, due date and time, assignee, type.
 * Accepting creates the task as the task form would; nothing is created until then.
 */
export default function TaskProposalDialog({ proposal, onClose, onAccept, saving }: Props) {
  const authContext = useAuthContext();
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [type, setType] = useState<TaskProposalType>('general');
  const [assignee, setAssignee] = useState<Option | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!proposal) return;
    setTitle(proposal.title);
    setDue(proposal.due_at ? moment(proposal.due_at).format('YYYY-MM-DDTHH:mm') : '');
    setType(proposal.type);
    setAssignee(proposal.assignee ? { id: proposal.assignee.id, name: proposal.assignee.name ?? '' } : null);
  }, [proposal]);

  const { currentData: accounts } = useGetAccountsQuery(
    { org_id: authContext?.organization?.id as string, page: { page: 1, per_page: 20 }, search_query: search },
    { skip: !proposal || !authContext }
  );
  const options = useMemo<Option[]>(
    () => (accounts?.results ?? []).map((account: any) => ({ id: account.id, name: `${account.first_name} ${account.last_name}` })),
    [accounts]
  );

  const submit = () =>
    onAccept({
      title: title.trim() || undefined,
      due_at: due ? moment(due).toISOString() : null,
      type,
      assigned_to: assignee?.id ?? null,
    });

  return (
    <Dialog open={!!proposal} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Edit task before adding</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth autoFocus inputProps={{ maxLength: 255 }} />
          <TextField
            label="Due"
            type="datetime-local"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField select label="Type" value={type} onChange={(e) => setType(e.target.value as TaskProposalType)} fullWidth>
            {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={options}
            value={assignee}
            onChange={(_e, value) => setAssignee(value)}
            onInputChange={(_e, value) => setSearch(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Assigned to" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton variant="contained" onClick={submit} loading={saving} disabled={!title.trim()}>
          Add task
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
